/**
 * Product module formatters — dates and list description preview.
 */

/** Strip tags / entities so table cells never render raw HTML (avoids layout breaks). */
export function productDescriptionPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * List preview — Angular `getTextExcerpt` (default 130 chars) after stripping HTML.
 */
export function productDescriptionPreview(html: string, maxLen = 130): string {
  const plain = productDescriptionPlainText(html)
  if (!plain) return ''
  return plain.length > maxLen ? `${plain.slice(0, maxLen).trim()}...` : plain
}

export function formatProductDate(value: string | number | null | undefined): string {
  if (!value && value !== 0) return ''
  try {
    let ts: number | string = value
    const num = typeof value === 'number' ? value : Number(value)
    if (!isNaN(num) && num > 0) {
      ts = num < 10_000_000_000 ? num * 1000 : num
    }
    const d = new Date(ts)
    if (isNaN(d.getTime())) return String(value)
    const pad = (n: number) => String(n).padStart(2, '0')
    const year = String(d.getFullYear()).slice(-2)
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    return `${month}/${day}/${year}`
  } catch {
    return value === undefined || value === null ? '' : String(value)
  }
}

/**
 * Angular comm-templates "Last Updated" pipe `date:'MM/dd/yyyy hh:mm a'`:
 * 4-digit year, 12-hour clock with leading zero, minutes, AM/PM — no seconds.
 */
export function formatProductDateTimeLong(value: string | number | null | undefined): string {
  if (!value && value !== 0) return ''
  try {
    let ts: number | string = value
    const num = typeof value === 'number' ? value : Number(value)
    if (!isNaN(num) && num > 0) {
      ts = num < 10_000_000_000 ? num * 1000 : num
    }
    const d = new Date(ts)
    if (isNaN(d.getTime())) return String(value)
    const pad = (n: number) => String(n).padStart(2, '0')
    const year = String(d.getFullYear())
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    let hours = d.getHours()
    const minutes = pad(d.getMinutes())
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    return `${month}/${day}/${year} ${pad(hours)}:${minutes} ${ampm}`
  } catch {
    return value === undefined || value === null ? '' : String(value)
  }
}

export function formatProductDateTime(value: string | number | null | undefined): string {
  if (!value && value !== 0) return ''
  try {
    let ts: number | string = value
    const num = typeof value === 'number' ? value : Number(value)
    if (!isNaN(num) && num > 0) {
      ts = num < 10_000_000_000 ? num * 1000 : num
    }
    const d = new Date(ts)
    if (isNaN(d.getTime())) return String(value)
    const pad = (n: number) => String(n).padStart(2, '0')
    const year = String(d.getFullYear()).slice(-2)
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    let hours = d.getHours()
    const minutes = pad(d.getMinutes())
    const seconds = pad(d.getSeconds())
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    return `${month}/${day}/${year} ${pad(hours)}:${minutes}:${seconds} ${ampm}`
  } catch {
    return value === undefined || value === null ? '' : String(value)
  }
}
